import React, { useState, useEffect, useRef } from 'react';
import { Button, Spinner, Badge, Form } from 'react-bootstrap';
import api from '../../services/api';
import './ChatModal.css';
const ChatModal = ({ show, onHide, onNewMessage }) => {
  const [messages, setMessages] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  useEffect(() => {
    if (show) {
      loadDepartments();
      loadMessages(true);
      markAsRead();
      refreshIntervalRef.current = setInterval(() => {
        loadMessages(true, true);
      }, 5000);
    }
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [show]);
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const loadDepartments = async () => {
    try {
      const response = await api.get('/chat/departments');
      setDepartments(response.data);
      const nocDept = response.data.find(d => d.id === 3);
      if (nocDept) {
        setSelectedDepartment(3);
      } else if (response.data.length > 0) {
        setSelectedDepartment(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };
  const loadMessages = async (reset = false, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const currentOffset = reset ? 0 : offset;
      const response = await api.get(`/chat/messages?limit=10&offset=${currentOffset}`);
      if (reset) {
        setMessages(response.data.messages);
        setOffset(10);
      } else {
        setMessages([...response.data.messages, ...messages]);
        setOffset(currentOffset + 10);
      }
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };
  const markAsRead = async () => {
    try {
      await api.put('/chat/messages/read');
      if (onNewMessage) onNewMessage();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };
  const handleSend = async () => {
    if (!newMessage.trim() || !selectedDepartment) return;
    setSending(true);
    try {
      const response = await api.post('/chat/messages', {
        msg: newMessage,
        department_id: selectedDepartment
      });
      setMessages([...messages, response.data.data]);
      setNewMessage('');
      if (onNewMessage) onNewMessage();
    } catch (error) {
      console.error('Failed to send message:', error);
      alert(error.response?.data?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };
  const handleDelete = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.delete(`/chat/messages/${messageId}`);
      setMessages(messages.filter(m => m.id !== messageId));
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete message');
    }
  };
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadMessages(false);
    }
  };
  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };
  const getCheckmarks = (msg) => {
    if (msg.direction === 0) return null;
    if (msg.is_read) return <span className="checkmarks read">✓✓</span>;
    if (msg.is_delivered) return <span className="checkmarks delivered">✓✓</span>;
    if (msg.is_sent) return <span className="checkmarks sent">✓</span>;
    return <span className="checkmarks sending">🕐</span>;
  };
  const canDelete = (msg) => {
    if (msg.direction !== 1) return false;
    const sentAt = new Date(msg.sent_at);
    const now = new Date();
    const diffMinutes = (now - sentAt) / 1000 / 60;
    return diffMinutes <= 2;
  };
  const getSelectedDeptName = () => {
    const dept = departments.find(d => d.id === parseInt(selectedDepartment));
    return dept ? dept.type_name : '';
  };
  if (!show) return null;
  return (
    <div className="chat-window">
      <div className="chat-header">
        <div className="chat-header-content">
          <h5>Support Chat with:</h5>
          <Form.Select size="sm" value={selectedDepartment} onChange={(e) => setSelectedDepartment(e.target.value)} className="department-select">
            {departments.map(dept => (
              <option key={dept.id} value={dept.id}>{dept.type_name}</option>
            ))}
          </Form.Select>
        </div>
        <button className="close-btn" onClick={onHide}>×</button>
      </div>
      <div className="chat-body-fixed">
        {hasMore && (
          <div className="text-center mb-3">
            <Button variant="link" size="sm" onClick={handleLoadMore} disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : 'Load more messages'}
            </Button>
          </div>
        )}
        <div className="messages-container" ref={messagesContainerRef}>
          {messages.map((msg) => (
            <div key={msg.id} className={`message ${msg.direction === 1 ? 'outgoing' : 'incoming'}`}>
              <div className="message-bubble">
                {msg.direction === 0 && (
                  <div className="message-department">
                    <Badge bg="secondary" className="mb-1">{msg.department_name}</Badge>
                  </div>
                )}
                <div className="message-text">{msg.msg}</div>
                <div className="message-footer">
                  <span className="message-time">{formatTime(msg.sent_at)}</span>
                  {getCheckmarks(msg)}
                  {canDelete(msg) && (
                    <button className="delete-btn" onClick={() => handleDelete(msg.id)}>×</button>
                  )}
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>
      <div className="chat-footer-fixed">
        <Form.Control type="text" placeholder="Type a message..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSend()} disabled={sending} />
        <Button variant="primary" onClick={handleSend} disabled={sending || !newMessage.trim()}>
          {sending ? <Spinner animation="border" size="sm" /> : 'Send'}
        </Button>
      </div>
    </div>
  );
};
export default ChatModal;